import type { Meta, StoryFn } from '@storybook/react';

import { ThemeOverride, icons, Sentiment } from '@pega/cosmos-react-core';
import type { SentimentProps } from '@pega/cosmos-react-core';

export default {
  title: 'Core/Sentiment',
  component: Sentiment,
  excludeStories: ['ConfigurableSentiment']
} as Meta;

interface SentimentStoryProps extends SentimentProps {
  positiveLabel: string;
  neutralLabel: string;
  negativeLabel: string;
  positiveIcon: string;
  neutralIcon: string;
  negativeIcon: string;
}

export const SentimentDemo: StoryFn<SentimentStoryProps> = (args: SentimentStoryProps) => {
  const iconNames = {
    positive: args.positiveIcon,
    neutral: args.neutralIcon,
    negative: args.negativeIcon
  };

  const labels = {
    positive: args.positiveLabel,
    neutral: args.neutralLabel,
    negative: args.negativeLabel
  };

  return (
    <Sentiment
      variant={args.variant}
      labelHidden={args.labelHidden}
      icons={iconNames}
      labels={labels}
    />
  );
};

SentimentDemo.args = {
  variant: 'positive',
  labelHidden: false,
  positiveLabel: 'Yay!',
  positiveIcon: 'face-happy-solid',
  neutralLabel: 'Hm…',
  neutralIcon: 'face-blank-solid',
  negativeLabel: 'Uh-oh!',
  negativeIcon: 'face-sad-solid'
};

SentimentDemo.argTypes = {
  variant: { options: ['positive', 'neutral', 'negative'], control: { type: 'select' } },
  labelHidden: { control: { type: 'boolean' } },
  positiveLabel: { control: { type: 'text' } },
  positiveIcon: { options: [...icons], control: { type: 'select', icons: true } },
  neutralLabel: { control: { type: 'text' } },
  neutralIcon: { options: [...icons], control: { type: 'select', icons: true } },
  negativeLabel: { control: { type: 'text' } },
  negativeIcon: { options: [...icons], control: { type: 'select', icons: true } }
};

interface ConfigurableSentimentProps {
  positiveColor?: string;
  negativeColor?: string;
  neutralColor?: string;
}

export const ConfigurableSentiment: StoryFn<ConfigurableSentimentProps> = (
  args: ConfigurableSentimentProps
) => {
  return (
    <ThemeOverride
      theme={{
        components: {
          sentiment: {
            positive: {
              color: args.positiveColor
            },
            negative: {
              color: args.negativeColor
            },
            neutral: {
              color: args.neutralColor
            }
          }
        }
      }}
    >
      <Sentiment variant='positive' icons={{ positive: 'face-happy-solid' }} />
      <Sentiment variant='negative' icons={{ negative: 'face-sad-solid' }} />
      <Sentiment variant='neutral' icons={{ neutral: 'face-blank-solid' }} />
    </ThemeOverride>
  );
};

ConfigurableSentiment.args = {
  positiveColor: '#20aa50',
  negativeColor: '#d91c29',
  neutralColor: '#939393'
};

ConfigurableSentiment.argTypes = {
  positiveColor: { control: { type: 'color' } },
  negativeColor: { control: { type: 'color' } },
  neutralColor: { control: { type: 'color' } }
};
